import { lazy, Suspense, useEffect, useId, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Info } from 'lucide-react';
import PanelCollapse from 'src/componentes/panel-collapse';
import Input from 'src/componentes/input';
import Switch from 'src/componentes/Form/Switch';
import LoadingScreen from 'src/componentes/LoadingScreen';
import useAuth from 'src/hooks/useAuth';
import { capitalizeFirstLetter } from 'src/utils/string';
import { STATUS_DOCUMENTO } from 'src/utils/constants';
import PublicoInteressadoCriterios from './PublicoInteressadoCriterios';
import {
  CONFIGURACAO_LEITURA_FIELDS as FIELDS,
  LEITURA_EDITOR_MODULES,
  PRAZO_LEITURA_PADRAO,
} from './configuracaoLeituraDocumento';

const ReactQuill = lazy(() => import('src/componentes/quill'));

export default function PublicoInteressadoLeituraSection({
  documentoId,
  getValue,
  onChangeValue,
  disabled = false,
  hasErrorField = () => false,
}) {
  const intl = useIntl();
  const { terms } = useAuth();
  const termosPublico = { publicointeressado: terms.publicointeressado };
  const id = useId();
  const envioAutomatico = !!getValue(FIELDS.ENVIO_AUTOMATICO);
  const tituloLabel = intl.formatMessage({ id: 'titulo' });
  const conteudoLabel = intl.formatMessage({ id: 'label.conteudo' });
  const statusAtual = getValue('statusAtual');
  const versao = getValue('ultimoVersionamentoManual');
  const versaoVigente =
    (statusAtual?.id ?? versao?.status?.id) === STATUS_DOCUMENTO.VIGENTE
      ? versao?.codigo
      : null;

  const aplicarPrazoPadrao = useRef(false);

  useEffect(() => {
    if (!aplicarPrazoPadrao.current) return;
    aplicarPrazoPadrao.current = false;
    if (getValue(FIELDS.PRAZO_DIAS_UTEIS) == null)
      onChangeValue(FIELDS.PRAZO_DIAS_UTEIS, PRAZO_LEITURA_PADRAO);
  });

  const toggleEnvioAutomatico = () => {
    if (disabled) return;
    aplicarPrazoPadrao.current = !envioAutomatico;
    onChangeValue(FIELDS.ENVIO_AUTOMATICO, !envioAutomatico);
  };

  return (
    <PanelCollapse
      open
      header={capitalizeFirstLetter(
        intl.formatMessage(
          {
            id: 'labelPublicoInteressadoConfirmacaoLeitura',
          },
          termosPublico
        )
      )}
    >
      <PublicoInteressadoCriterios
        documentoId={documentoId}
        value={getValue('criteriosPublicoInteressado')}
        onChange={(value) =>
          onChangeValue('criteriosPublicoInteressado', value)
        }
        disabled={disabled}
      />
      <PanelCollapse
        open={false}
        header={intl.formatMessage({ id: 'labelEnvioPendenciaLeitura' })}
      >
        <div className="tw:mb-4 tw:flex tw:items-start tw:justify-between tw:gap-4">
          <div>
            <label
              htmlFor={`${id}-envio-switch`}
              id={`${id}-envio`}
              className="tw:mb-1 tw:block tw:text-sm"
            >
              {intl.formatMessage(
                {
                  id: 'labelEnviarPendenciaLeituraAutomaticamente',
                },
                termosPublico
              )}
            </label>
            <p
              id={`${id}-ajuda`}
              className="tw:mb-0 tw:text-xs tw:text-muted-foreground"
            >
              {intl.formatMessage(
                { id: 'msgEnvioAutomaticoPendenciaLeitura' },
                termosPublico
              )}
            </p>
          </div>
          <Switch
            id={`${id}-envio-switch`}
            role="switch"
            checked={envioAutomatico}
            aria-labelledby={`${id}-envio`}
            aria-describedby={`${id}-ajuda`}
            disabled={disabled}
            onChange={toggleEnvioAutomatico}
          />
        </div>
        <div className="tw:mb-4">
          <label
            htmlFor={`${id}-prazo`}
            className="tw:mb-1 tw:block tw:text-sm"
          >
            {intl.formatMessage({ id: 'labelPrazoPadraoConfirmacaoLeitura' })} *
          </label>
          <div className="tw:flex tw:items-center tw:gap-2">
            <div className="tw:w-24 tw:[&_.mb-3]:mb-0">
              <Input
                id={`${id}-prazo`}
                type="number"
                min={1}
                step={1}
                required
                showLabel={false}
                model={{
                  value: String(
                    getValue(FIELDS.PRAZO_DIAS_UTEIS) ?? PRAZO_LEITURA_PADRAO
                  ),
                  errors: !!hasErrorField(FIELDS.PRAZO_DIAS_UTEIS),
                  requestChange: (value) =>
                    onChangeValue(
                      FIELDS.PRAZO_DIAS_UTEIS,
                      value === '' ? '' : Number(value)
                    ),
                }}
                aria-invalid={!!hasErrorField(FIELDS.PRAZO_DIAS_UTEIS)}
                aria-describedby={`${id}-dias`}
                disabled={disabled}
              />
            </div>
            <span id={`${id}-dias`} className="tw:text-sm">
              {intl.formatMessage({ id: 'diasUteis' })}
            </span>
          </div>
        </div>
        <div className="tw:mb-4">
          <Input
            id={`${id}-titulo`}
            type="text"
            className="tw:bg-transparent"
            autoComplete="off"
            maxLength={100}
            model={{
              label: tituloLabel,
              value: getValue(FIELDS.TITULO_EMAIL) ?? '',
              requestChange: (value) =>
                onChangeValue(FIELDS.TITULO_EMAIL, value),
              errors: !!hasErrorField(FIELDS.TITULO_EMAIL),
            }}
            aria-invalid={!!hasErrorField(FIELDS.TITULO_EMAIL)}
            required
            disabled={disabled}
          />
        </div>
        <div className="tw:mb-4">
          <div id={`${id}-conteudo`} className="tw:mb-1 tw:text-sm">
            {conteudoLabel} *
          </div>
          <div
            role="group"
            aria-labelledby={`${id}-conteudo`}
            aria-invalid={!!hasErrorField(FIELDS.CONTEUDO_EMAIL)}
            className="tw:rounded-md tw:aria-invalid:ring-1 tw:aria-invalid:ring-destructive tw:[&_.ql-editor]:min-h-32"
          >
            <Suspense fallback={<LoadingScreen />}>
              <ReactQuill
                theme="snow"
                modules={LEITURA_EDITOR_MODULES}
                maxLength={1000}
                value={getValue(FIELDS.CONTEUDO_EMAIL) ?? ''}
                onChange={(value) => {
                  if (!disabled) onChangeValue(FIELDS.CONTEUDO_EMAIL, value);
                }}
                readOnly={disabled}
              />
            </Suspense>
          </div>
        </div>
        <div className="tw:flex tw:items-start tw:gap-2 tw:rounded-md tw:bg-primary/10 tw:px-3 tw:py-2 tw:text-xs tw:text-primary">
          <Info aria-hidden="true" className="tw:size-4 tw:shrink-0" />
          <span>
            {intl.formatMessage({ id: 'msgEnvioLeituraSomenteVersaoVigente' })}
            {versaoVigente && (
              <>
                {' '}
                {intl.formatMessage(
                  { id: 'msgVersaoVigenteAtual' },
                  { versao: versaoVigente }
                )}
              </>
            )}
          </span>
        </div>
      </PanelCollapse>
    </PanelCollapse>
  );
}
